import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { functions } from "../config/firebase";
import { useUserRole } from "../hooks/useUserRole";

interface Tenant {
  id: string;
  name: string;
  logo?: string;
}

interface OnboardingModalProps {
  isOpen: boolean;
  onComplete?: () => void;
}

/**
 * OnboardingModal Component - Phase 3
 * 
 * Guides new users through onboarding:
 * 1. Select club/tenant from dropdown (getTenantsList)
 * 2. Enter member number (1-7 digits)
 * 3. Live preview: "524" → "000524" + wallet ID "73-000524"
 * 4. Submit to completeOnboarding() Cloud Function
 * 5. On success: Close modal, trigger role reload
 * 
 * Error handling:
 * - 404: Tenant not found
 * - 409: Member number already in use
 * - 400: Invalid member number format
 * - 401: Not authenticated
 * 
 * ARCHITECTURE: Calls Cloud Function which updates /users/{uid} in Firestore
 */
export function OnboardingModal({ isOpen, onComplete }: OnboardingModalProps) {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [memberNumber, setMemberNumber] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantLoading, setTenantLoading] = useState(false);
  
  const auth = getAuth();
  const { user } = useUserRole();

  // Fetch tenant list on mount
  useEffect(() => {
    if (!isOpen) return;

    const fetchTenants = async () => {
      try {
        setTenantLoading(true);
        const getTenantsList = httpsCallable(functions, "get_tenants_list");
        const response = await getTenantsList();
        const data = response.data as Tenant[];
        setTenants(data || []);
      } catch (err) {
        console.error("Failed to fetch tenants:", err);
        setError("Could not load clubs. Please try again.");
      } finally {
        setTenantLoading(false);
      }
    };

    fetchTenants();
  }, [isOpen]);

  // Format member number: "524" → "000524"
  const formatMemberNumber = (num: string): string => {
    return num.padStart(7, "0");
  };

  // Get wallet ID preview: "73-000524"
  const getWalletIdPreview = (): string | null => {
    if (!selectedTenantId || !memberNumber) return null;
    return `${selectedTenantId}-${formatMemberNumber(memberNumber)}`;
  };

  const handleMemberNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow 1-7 digits
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 7) {
      setMemberNumber(value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTenantId) {
      setError("Please select a club");
      return;
    }

    if (!memberNumber) {
      setError("Please enter a member number");
      return;
    }

    if (memberNumber.length === 0) {
      setError("Member number must be 1-7 digits");
      return;
    }

    try {
      setLoading(true);

      const completeOnboarding = httpsCallable(
        functions,
        "complete_onboarding"
      );

      const response = await completeOnboarding({
        tenantId: selectedTenantId,
        memberID: memberNumber,
      });

      const data = response.data as any;

      if (data?.success) {
        // Onboarding complete - refresh user role and close modal
        if (onComplete) {
          onComplete();
        }
      } else {
        setError(data?.error || "Failed to complete onboarding");
      }
    } catch (err: any) {
      console.error("Onboarding error:", err);
      
      // Handle specific error codes
      if (err.code === "not-found") {
        setError("Club not found. Please select a valid club.");
      } else if (err.code === "already-exists") {
        setError("This member number is already in use for this club.");
      } else if (err.code === "invalid-argument") {
        setError("Invalid member number format (1-7 digits only).");
      } else if (err.code === "unauthenticated") {
        setError("Session expired. Please log in again.");
      } else {
        setError(err.message || "An error occurred during onboarding");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const walletIdPreview = getWalletIdPreview();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
        <h2 className="text-2xl font-bold mb-6 text-gray-900">Welcome to GaveKort</h2>
        
        <p className="text-gray-600 mb-6">
          Complete your profile to get started. Please select your club and enter your member number.
        </p>

        <form onSubmit={handleSubmit}>
          {/* Club Selection */}
          <div className="mb-6">
            <label htmlFor="tenant" className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Club *
            </label>
            <select
              id="tenant"
              value={selectedTenantId}
              onChange={(e) => {
                setSelectedTenantId(e.target.value);
                setError(null);
              }}
              disabled={tenantLoading || loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
            >
              <option value="">
                {tenantLoading ? "Loading clubs..." : "Choose a club..."}
              </option>
              {tenants.map((tenant) => (
                <option key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </option>
              ))}
            </select>
          </div>

          {/* Member Number Input */}
          <div className="mb-6">
            <label htmlFor="memberNumber" className="block text-sm font-medium text-gray-700 mb-2">
              Member Number *
            </label>
            <input
              id="memberNumber"
              type="text"
              inputMode="numeric"
              placeholder="Enter 1-7 digits"
              value={memberNumber}
              onChange={handleMemberNumberChange}
              disabled={loading}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              maxLength={7}
            />
            <p className="text-xs text-gray-500 mt-1">Example: 524 (will be formatted as 000524)</p>
          </div>

          {/* Wallet ID Preview */}
          {walletIdPreview && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-gray-600 mb-1">Your Wallet ID:</p>
              <p className="text-lg font-mono font-bold text-blue-600">{walletIdPreview}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || tenantLoading || !selectedTenantId || !memberNumber}
            className="w-full bg-blue-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Completing Setup..." : "Complete Setup"}
          </button>
        </form>

        {/* Info Text */}
        <p className="text-xs text-gray-500 text-center mt-6">
          Your member number is used to link your digital wallet with your club account.
        </p>
      </div>
    </div>
  );
}
